import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import {
  closeModal,
  Modal,
  Select,
  TextInput,
  UploadedFileBanner,
} from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { DocUploadIcon } from "../../../svg/doc-upload-icon";
import type { CreateSupportTicketFormData } from "../supportTicketTypes";
import {
  ATTACHMENT_ACCEPT,
  isValidSupportTicketAttachment,
  SUPPORTED_ATTACHMENT_COPY,
} from "../utils/validateSupportTicketAttachment";

const PRIORITY_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
];

const EMPTY_FORM: CreateSupportTicketFormData = {
  issueTitle: "",
  priority: "",
  patientName: "",
  mrn: "",
  description: "",
  attachment: null,
};

interface CreateNewTicketModalProps {
  /** Submits the create-ticket form to the API. Returns true on success. */
  onCreate?: (data: CreateSupportTicketFormData) => Promise<boolean>;
  /** Whether the create-ticket request is in flight. */
  isCreating?: boolean;
}

/**
 * Modal for creating a new support ticket with optional attachment upload.
 * Opened imperatively with `openModal(ModalId.CREATE_NEW_TICKET_MODAL)`.
 *
 * @param onCreate - Optional callback invoked with the collected form payload.
 * @returns Create New Ticket modal registered with Oasis via `dialogId`.
 */
const CreateNewTicketModal = ({
  onCreate,
  isCreating = false,
}: CreateNewTicketModalProps) => {
  const [form, setForm] = useState<CreateSupportTicketFormData>(EMPTY_FORM);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateField = <K extends keyof CreateSupportTicketFormData>(
    field: K,
    value: CreateSupportTicketFormData[K],
  ) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const close = () => {
    resetForm();
    closeModal(ModalId.CREATE_NEW_TICKET_MODAL);
  };

  const handleFileSelection = (file: File | null) => {
    if (!file) {
      updateField("attachment", null);
      return;
    }

    if (!isValidSupportTicketAttachment(file)) {
      toast.error(`Supported formats: ${SUPPORTED_ATTACHMENT_COPY}.`);
      return;
    }

    updateField("attachment", file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    handleFileSelection(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] ?? null;
    handleFileSelection(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDeleteAttachment = () => {
    updateField("attachment", null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreate = async () => {
    const wasCreated = await onCreate?.(form);
    if (wasCreated) {
      close();
    }
  };

  const isCreateDisabled =
    isCreating ||
    form.issueTitle.trim().length === 0 ||
    form.description.trim().length === 0;

  return (
    <Modal
      dialogId={ModalId.CREATE_NEW_TICKET_MODAL}
      title="Create New Ticket"
      autoCloseOnSave={false}
      onClose={close}
      onCancel={close}
      onSave={handleCreate}
      saveButtonText={isCreating ? "Creating..." : "Create"}
      cancelText="Cancel"
      disableSave={isCreateDisabled}
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            id="create-ticket-issue-title"
            label="Issue Title"
            placeholder="Enter Issue Title"
            required
            defaultValue={form.issueTitle}
            onChange={(data) => updateField("issueTitle", data.value ?? "")}
            onBlur={(data) => updateField("issueTitle", data.value ?? "")}
          />
          <Select
            id="create-ticket-priority"
            label="Priority"
            placeholder="Select"
            options={PRIORITY_OPTIONS}
            value={form.priority}
            onOptionChange={(option) =>
              updateField("priority", option.value ?? "")
            }
          />
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-3">
          <TextInput
            id="create-ticket-patient-name"
            label="Patient Name"
            placeholder="Enter Patient Name"
            defaultValue={form.patientName}
            onChange={(data) => updateField("patientName", data.value ?? "")}
            onBlur={(data) => updateField("patientName", data.value ?? "")}
          />
          <span
            className="pb-3 text-xs font-bold uppercase tracking-wide text-primaryGray-7"
            aria-hidden="true"
          >
            or
          </span>
          <TextInput
            id="create-ticket-mrn"
            label="MRN"
            placeholder="Enter MRN"
            defaultValue={form.mrn}
            onChange={(data) => updateField("mrn", data.value ?? "")}
            onBlur={(data) => updateField("mrn", data.value ?? "")}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="create-ticket-description"
            className="text-tiny font-medium leading-[1.125rem] text-primaryGray-2"
          >
            Description <span className="text-tertiaryRed-5">*</span>
          </label>
          <textarea
            id="create-ticket-description"
            value={form.description}
            onChange={(event) => updateField("description", event.target.value)}
            placeholder="Enter Description"
            rows={4}
            className="min-h-[6.5rem] w-full resize-none rounded border border-primaryGray-14 px-3 py-3 text-tiny font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2 outline-none placeholder:text-primaryGray-7"
          />
        </div>

        <div className="drag-drop-container">
          {!form.attachment && (
            <div
              className="drop-file-input"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <DocUploadIcon />
              <div className="main-text">
                Drag and drop or <span>click here</span> to upload attachment
              </div>
              <div className="sub-text">
                Supported formats: {SUPPORTED_ATTACHMENT_COPY}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                onChange={handleFileChange}
                id="create-ticket-attachment"
                name="create-ticket-attachment"
              />
            </div>
          )}
        </div>

        {form.attachment && (
          <div className="uploaded-file-container">
            <UploadedFileBanner
              fileName={form.attachment.name}
              deleteUploadedFile={handleDeleteAttachment}
              currentProgress={100}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateNewTicketModal;
